/**
 * Conversation-level 1–5 star feedback.
 */

import { Icons } from './utils/icons.js';
import { t } from '../../i18n.js';

/**
 * @param {Object} options
 * @param {number|null} options.rating
 * @param {function(number, string=): void} options.onSubmit
 * @param {boolean} [options.disabled]
 * @returns {HTMLElement}
 */
export function createConversationFeedback({ rating = null, onSubmit, disabled = false }) {
  const root = document.createElement('div');
  root.className = 'ai-conversation-feedback';

  const label = document.createElement('span');
  label.className = 'ai-conversation-feedback__label';
  label.textContent = t('ai_feedback_conversation_title');

  const stars = document.createElement('div');
  stars.className = 'ai-conversation-feedback__stars';

  let hovered = null;
  let noteOpen = false;
  let pendingRating = null;

  const starButtons = [1, 2, 3, 4, 5].map((star) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ai-conversation-feedback__star';
    btn.setAttribute('aria-label', t('ai_feedback_rate_stars', { n: String(star) }));
    btn.disabled = disabled;
    btn.onmouseenter = () => {
      if (disabled) return;
      hovered = star;
      paint();
    };
    btn.onmouseleave = () => {
      hovered = null;
      paint();
    };
    btn.onclick = () => {
      if (disabled) return;
      pendingRating = star;
      openNote();
    };
    return { star, btn };
  });

  function paint() {
    const active = hovered ?? rating ?? 0;
    starButtons.forEach(({ star, btn }) => {
      const filled = star <= active;
      btn.classList.toggle('is-filled', filled);
      btn.innerHTML = filled ? Icons.star() : Icons.starOutline();
      btn.style.transform =
        hovered !== null && star <= hovered ? 'translateY(-1px)' : 'translateY(0)';
    });
  }

  function closeNote(submitWithoutNote) {
    if (noteOpen && pendingRating && submitWithoutNote) {
      onSubmit(pendingRating);
    }
    noteOpen = false;
    pendingRating = null;
    const pop = root.querySelector('.ai-feedback-note-popover');
    pop?.remove();
  }

  function openNote() {
    closeNote(false);
    noteOpen = true;

    const pop = document.createElement('div');
    pop.className = 'ai-feedback-note-popover ai-conversation-feedback__note';

    const prompt = document.createElement('p');
    prompt.className = 'ai-feedback-note-popover__prompt';
    prompt.textContent = t('ai_feedback_note_prompt');

    const textarea = document.createElement('textarea');
    textarea.className = 'ai-feedback-note-popover__input';
    textarea.placeholder = t('ai_feedback_note_placeholder');
    textarea.maxLength = 1000;
    textarea.rows = 3;

    const actions = document.createElement('div');
    actions.className = 'ai-feedback-note-popover__actions';

    const skip = document.createElement('button');
    skip.type = 'button';
    skip.className = 'ai-feedback-note-popover__btn';
    skip.textContent = t('ai_feedback_skip');
    skip.onclick = () => {
      if (!pendingRating) return;
      onSubmit(pendingRating);
      pendingRating = null;
      closeNote(false);
    };

    const submit = document.createElement('button');
    submit.type = 'button';
    submit.className = 'ai-feedback-note-popover__btn primary';
    submit.textContent = t('ai_feedback_submit');
    submit.onclick = () => {
      if (!pendingRating) return;
      const trimmed = textarea.value.trim();
      onSubmit(pendingRating, trimmed || undefined);
      pendingRating = null;
      closeNote(false);
    };

    actions.append(skip, submit);
    pop.append(prompt, textarea, actions);
    root.appendChild(pop);
    textarea.focus();
  }

  starButtons.forEach(({ btn }) => stars.appendChild(btn));
  root.append(label, stars);
  paint();

  root.update = ({ rating: nextRating, disabled: nextDisabled } = {}) => {
    if (nextRating !== undefined) rating = nextRating;
    if (nextDisabled !== undefined) {
      disabled = nextDisabled;
      starButtons.forEach(({ btn }) => {
        btn.disabled = disabled;
      });
    }
    paint();
  };

  return root;
}
